export const BRAND = { name: "Seutrade Direct" };

export const STATES = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"], ["CA", "California"],
  ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"], ["DC", "District of Columbia"],
  ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"], ["ID", "Idaho"], ["IL", "Illinois"],
  ["IN", "Indiana"], ["IA", "Iowa"], ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"],
  ["ME", "Maine"], ["MD", "Maryland"], ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"],
  ["MS", "Mississippi"], ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"], ["NV", "Nevada"],
  ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"], ["NY", "New York"],
  ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"], ["OK", "Oklahoma"], ["OR", "Oregon"],
  ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"], ["SD", "South Dakota"],
  ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"], ["VT", "Vermont"], ["VA", "Virginia"],
  ["WA", "Washington"], ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"],
].map(([code, name]) => ({ code, name }));

export const stateName = (code) => STATES.find((s) => s.code === code)?.name || code;

const NO_SR22 = ["DE", "KY", "MN", "NM", "NY", "NC", "OK", "PA"];
const FR44 = ["FL", "VA"];

export const stateNote = (code) => {
  if (NO_SR22.includes(code)) return "This state doesn't use the SR22 form. We file the equivalent proof of financial responsibility your state requires.";
  if (FR44.includes(code)) return "DUI-related cases in this state usually require an FR-44 filing with higher liability limits. We handle both SR22 and FR-44.";
  return "We file your SR22 electronically with the state's motor vehicle agency.";
};

export const REASONS = [
  { value: "dui", label: "DUI / DWI" },
  { value: "reckless", label: "Reckless driving" },
  { value: "uninsured", label: "Driving without insurance" },
  { value: "points", label: "Too many points / tickets" },
  { value: "suspension", label: "License suspension or revocation" },
  { value: "other", label: "Other court or DMV order" },
  { value: "none", label: "I don't need an SR22 (regular policy)" },
];

export const LEVELS = [
  { value: "liability", label: "Liability only", desc: "Covers injuries and damage you cause to others." },
  { value: "full", label: "Full coverage", desc: "Liability plus collision & comprehensive for your vehicle.", ownerOnly: true },
];

export const AUTO_LIMITS = [
  { value: "10_20_10", label: "$10,000 / $20,000 / $10,000", desc: "Starter limits — lowest price" },
  { value: "25_50_25", label: "$25,000 / $50,000 / $25,000", desc: "Common state minimum" },
  { value: "50_100_50", label: "$50,000 / $100,000 / $50,000", desc: "More protection" },
  { value: "100_300_100", label: "$100,000 / $300,000 / $100,000", desc: "Strong protection" },
];

export const COMMERCIAL_LIMITS = [
  { value: "100k_csl", label: "$100,000 combined single limit", desc: "Small business starter" },
  { value: "300k_csl", label: "$300,000 combined single limit", desc: "Most common for contractors" },
  { value: "500k_csl", label: "$500,000 combined single limit", desc: "Higher protection" },
  { value: "1m_csl", label: "$1,000,000 combined single limit", desc: "Often required by contracts and shippers" },
];

export const GL_LIMITS = [
  { value: "300k_600k", label: "$300,000 / $600,000", desc: "Per occurrence / aggregate — starter" },
  { value: "500k_1m", label: "$500,000 / $1,000,000", desc: "Per occurrence / aggregate" },
  { value: "1m_2m", label: "$1,000,000 / $2,000,000", desc: "Most common — often required by contracts and leases" },
  { value: "2m_4m", label: "$2,000,000 / $4,000,000", desc: "Higher protection" },
];

export const GL_BUSINESS_TYPES = [
  { value: "contractor", label: "Contractor / trades" },
  { value: "retail", label: "Retail store" },
  { value: "restaurant", label: "Restaurant / food service" },
  { value: "professional", label: "Office / professional services" },
  { value: "cleaning", label: "Cleaning / janitorial" },
  { value: "landscaping", label: "Landscaping" },
  { value: "other", label: "Other business" },
];

export const BUSINESS_TYPES = [
  { value: "contractor", label: "Contractor / trades" },
  { value: "delivery", label: "Delivery / courier" },
  { value: "trucking", label: "Trucking / hauling" },
  { value: "rideshare", label: "Rideshare / taxi / limo" },
  { value: "landscaping", label: "Landscaping" },
  { value: "other", label: "Other business use" },
];

export const limitsFor = (type) => (type === "general_liability" ? GL_LIMITS : type === "commercial" ? COMMERCIAL_LIMITS : AUTO_LIMITS);
export const businessTypesFor = (type) => (type === "general_liability" ? GL_BUSINESS_TYPES : BUSINESS_TYPES);
export const limitLabel = (v) => [...AUTO_LIMITS, ...COMMERCIAL_LIMITS, ...GL_LIMITS].find((l) => l.value === v)?.label || "State minimum";

export const FILING_STEPS = [
  { key: "payment_received", label: "Payment received", desc: "Policy bound and paid." },
  { key: "filing_submitted", label: "SR22 filed with state", desc: "E-filing sent to the motor vehicle agency." },
  { key: "state_processing", label: "State processing", desc: "Agency is reviewing the filing." },
  { key: "certificate_active", label: "Certificate active", desc: "Filing accepted. Proof ready to download." },
];

export const FILING_LABEL = {
  payment_received: "Payment received",
  filing_submitted: "Filed with state",
  state_processing: "State processing",
  certificate_active: "Certificate active",
  cancelled: "Cancelled (SR26 filed)",
  not_required: "No filing required",
};

export const QUOTE_LABEL = {
  submitted: "Under review",
  priced: "Price ready",
  purchased: "Purchased",
  declined: "Declined",
};

export const levelLabel = (v) => LEVELS.find((l) => l.value === v)?.label || "Liability only";
export const reasonLabel = (v) => REASONS.find((r) => r.value === v)?.label || v;
export const coverageLabel = (v) => ({ owner: "Personal Auto", non_owner: "Non-Owner Auto", commercial: "Commercial Auto Liability", general_liability: "General Liability" }[v] || v);
export const FILING_FEE = 25;
export const PIF_DISCOUNT = 0.1;

export const planAmounts = (monthly, plan, fee = FILING_FEE) => {
  if (plan === "full") {
    const total = Math.round(monthly * 12 * (1 - PIF_DISCOUNT) * 100) / 100;
    return { total, dueToday: total + fee, savings: Math.round((monthly * 12 - total) * 100) / 100 };
  }
  return { total: monthly * 12, dueToday: monthly + fee, savings: 0 };
};

export const BILLING_LABEL = { paid_up: "Paid up", due: "Payment due", past_due: "Past due" };

export const METHOD_LABEL = {
  card_online: "Card (online)",
  card: "Card (in person)",
  cash: "Cash",
  check: "Check",
  money_order: "Money order",
  adjustment: "Adjustment",
};

export const ENTRY_LABEL = { payment: "Payment", refund: "Refund", charge: "Charge", credit: "Credit" };

export const DOC_TYPES = [
  { value: "court_order", label: "Court order" },
  { value: "dmv_letter", label: "DMV / state letter" },
  { value: "license", label: "Driver's license" },
  { value: "claim_photo", label: "Claim photo" },
  { value: "police_report", label: "Police / incident report" },
  { value: "state_id", label: "State ID" },
  { value: "other", label: "Other" },
];

export const CLAIM_LABEL = { submitted: "Submitted", in_review: "In review", info_needed: "Info needed", approved: "Approved", denied: "Denied", closed: "Closed" };

export const AUTO_CLAIM_TYPES = [
  { value: "collision", label: "Collision / accident" },
  { value: "hit_and_run", label: "Hit and run" },
  { value: "theft", label: "Theft" },
  { value: "vandalism", label: "Vandalism" },
  { value: "weather", label: "Weather / hail / flood" },
  { value: "glass", label: "Glass damage" },
  { value: "injury", label: "Injury to another person" },
  { value: "other", label: "Other" },
];

export const GL_CLAIM_TYPES = [
  { value: "bodily_injury", label: "Someone was injured" },
  { value: "property_damage", label: "Damage to someone else's property" },
  { value: "advertising_injury", label: "Personal or advertising injury" },
  { value: "product", label: "Product or completed work" },
  { value: "other", label: "Other" },
];

export const claimTypesFor = (coverageType) => (coverageType === "general_liability" ? GL_CLAIM_TYPES : AUTO_CLAIM_TYPES);
export const claimTypeLabel = (coverageType, v) => claimTypesFor(coverageType).find((t) => t.value === v)?.label || v;
